import React, { useState } from "react";
import moment from "moment";
import "moment/locale/id";
import { useNavigate } from "react-router-dom";
import { isPastEvent } from "../../../utils/sortSeriesByDate";
import { getStaticImg } from "../../../utils/function";
import analytics from "../../../utils/analytics";
import BottomSheet from "../FormRegistration/components/BottomSheet/BottomSheet";

moment.locale("id");

// Compact uppercase range like the Figma: "25 - 27 SEP" (same month) or
// "29 SEP - 01 OKT" (crosses month). id-locale abbrevs → SEP/OKT/NOV/JAN.
// "-" when a date is missing (qualification_* is null across the sample data).
const fmtRange = (start, end) => {
  if (!start || !end) return "-";
  const a = moment(start);
  const b = moment(end);
  const day = (m) => m.format("DD");
  const mon = (m) => m.format("MMM").toUpperCase();
  if (a.isSame(b, "day")) return `${day(a)} ${mon(a)}`;
  return a.isSame(b, "month")
    ? `${day(a)} - ${day(b)} ${mon(b)}`
    : `${day(a)} ${mon(a)} - ${day(b)} ${mon(b)}`;
};

// Figma has two card shells; the badge/CTA/shell walk through five date states:
//   A belum mulai → dark "AKAN DATANG", no CTA         (before Kualifikasi)
//   B kualifikasi → light "BERLANGSUNG" + Lihat Detail (inside Kualifikasi)
//   C in-between  → dark "AKAN DATANG" + Lihat Detail  (Kualifikasi done, Series not open)
//   D series      → light "BERLANGSUNG" + Lihat Detail (inside Series)
//   E selesai     → light "SELESAI" + Lihat Detail     (after Series ends)
// The CTA can't key off the shell (A and C are both dark, only C shows it), so
// cardState returns an explicit `cta` flag decided by the dates.
const inWindow = (start, end) =>
  !!start &&
  !!end &&
  moment().isSameOrAfter(moment(start), "day") &&
  moment().isSameOrBefore(moment(end), "day");

export const cardState = (e) => {
  const now = moment();

  // B / D — BERLANGSUNG while today sits inside the Kualifikasi or Series window.
  if (
    inWindow(e?.qualification_start_date, e?.qualification_end_date) ||
    inWindow(e?.event_start_date, e?.event_end_date)
  )
    return { dark: false, cta: true, badge: "BERLANGSUNG", badgeBg: "#AF183C" };

  // E — Series is over: light "SELESAI" card, detail still reachable.
  if (isPastEvent(e))
    return { dark: false, cta: true, badge: "SELESAI", badgeBg: "#6A7481" };

  // A / C — dark "AKAN DATANG". Show Lihat Detail once Kualifikasi has ended
  // (the in-between gap, state C); hide it before anything starts (state A).
  const qe = e?.qualification_end_date;
  const cta = !!qe && now.isAfter(moment(qe), "day");
  return { dark: true, cta, badge: "AKAN DATANG", badgeBg: "#1F2D3D" };
};

// A card renders exactly one Kualifikasi range + one Series range, so cities
// only share a card when both windows match. Group by those four dates; an
// event without a series window can't merge (keyed by id). The API has no
// grouping field — matching windows is the only signal. Input is pre-sorted, so
// same-window cities are adjacent and Map insertion order keeps groups sorted.
const groupByWindow = (events) => {
  // Day granularity — cards show day ranges, so same-day/different-time events
  // (e.g. Jakarta 00:00 vs Yogyakarta 07:00) still belong together.
  const dayKey = (d) => (d ? moment(d).format("YYYY-MM-DD") : "");
  const map = new Map();
  for (const e of events) {
    const key = e?.event_start_date
      ? [
          dayKey(e?.qualification_start_date),
          dayKey(e?.qualification_end_date),
          dayKey(e.event_start_date),
          dayKey(e?.event_end_date),
        ].join("|")
      : `solo-${e?.id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(e);
  }
  return [...map.values()];
};

// Cards are ordered by when Kualifikasi opens (soonest first); a city without a
// Kualifikasi window falls back to its Series start, and a fully-finished series
// still sinks to the back. groupByWindow relies on this pre-sort to keep
// same-window cities adjacent.
const qualStart = (e) => {
  const d = e?.qualification_start_date || e?.event_start_date;
  return d ? moment(d).valueOf() : Infinity;
};

export const sortByQualificationDate = (events) =>
  [...events].sort((a, b) => {
    const aPast = isPastEvent(a);
    const bPast = isPastEvent(b);
    if (aPast !== bPast) return aPast ? 1 : -1;
    return qualStart(a) - qualStart(b);
  });

// A group shares one date window, so state is the same for every city in it.
// widthClass differs between contexts: the grid uses 2-col/194px, the "Pilih
// Kota" popup uses full-width rows (mobile) / 194px side-by-side (desktop).
// clickable turns the whole card into the tap target (popup) vs. button-only (grid).
const GroupCard = ({
  group,
  onDetail,
  clickable = false,
  widthClass = "w-[calc(50%-4px)] md:w-[194px]",
}) => {
  const rep = group[0];
  const { dark, cta, badge, badgeBg } = cardState(rep);

  const shell = dark
    ? {
        background: "linear-gradient(180deg,#01437F 0%,#002443 100%)",
        borderColor: "rgba(255,255,255,0.9)",
        color: "#FFFFFF",
      }
    : {
        background: "linear-gradient(180deg,#FFFFFF 0%,#BCF0FF 100%)",
        borderColor: "#00BCF1",
        color: "#1F2D3D",
      };

  return (
    <div
      onClick={clickable ? () => onDetail(group) : undefined}
      role={clickable ? "button" : undefined}
      className={`${widthClass} relative flex flex-col items-center gap-4 rounded-[24px] border-[1.5px] border-solid text-center p-3 md:px-6 md:py-4${
        clickable
          ? " group cursor-pointer transition-transform duration-150 hover:-translate-y-0.5"
          : ""
      }`}
      style={{
        ...shell,
        boxShadow:
          "0 0 5px rgba(255,255,255,0.6), inset 0 0 6px rgba(255,255,255,0.35)",
      }}
    >
      {/* inline-flex + items-center: Safari mobile mis-renders vertical padding
          on an inline <span> with a tight line-height, pushing the label off
          center. Centering it in a flex box keeps the 17px chip even everywhere. */}
      <span
        className="inline-flex items-center rounded-[4px] px-1 py-0.5 text-[11px] leading-[13px] font-bold text-white whitespace-nowrap"
        style={{ backgroundColor: badgeBg }}
      >
        {badge}
      </span>

      {/* One city name per line, like the Figma's stacked City-Name field.
          Owners Narrow (condensed) is the Figma display face — bundled via
          @font-face in index.css; it fits the design's 24px in the narrow card
          (widest name "BANJARMASIN" = 130px < 135px). break-words is a safety net. */}
      <div
        className="min-h-[48px] flex flex-col items-center justify-center text-[24px] leading-[24px] font-bold uppercase tracking-[0.25px] md:tracking-[0.5px] break-words"
        style={{ fontFamily: '"Owners Narrow", "Gilroy", sans-serif' }}
      >
        {group.map((c) => (
          <span key={c?.id}>{c?.region_name}</span>
        ))}
      </div>

      {/* Grid + desktop popup cards are narrow (194px), so the rows fill the card
          edge-to-edge. The mobile popup card is full-width, where the Figma wants a
          centered block with both values column-aligned: w-auto shrinks the block to
          its widest row and justify-between right-aligns both values (robust to
          longer ranges like "29 SEP - 01 OKT"); md:w-full restores the fill. */}
      <div
        className={`flex flex-col gap-1 md:gap-2 text-[11px] leading-[13px] uppercase ${
          clickable ? "w-auto md:w-full" : "w-full"
        }`}
      >
        <div className="flex items-center justify-between gap-x-4">
          <span className="font-normal">Kualifikasi</span>
          <span className="font-bold">
            {fmtRange(
              rep?.qualification_start_date,
              rep?.qualification_end_date,
            )}
          </span>
        </div>
        <div className="flex items-center justify-between gap-x-4">
          <span className="font-normal">Series</span>
          <span className="font-bold">
            {fmtRange(rep?.event_start_date, rep?.event_end_date)}
          </span>
        </div>
      </div>

      {cta && (
        // In the grid this opens the "Pilih Kota" popup for the whole group;
        // inside the popup the card itself is the tap target, so the button is
        // visual-only (pointer-events-none) and the click bubbles to the card.
        <button
          type="button"
          onClick={clickable ? undefined : () => onDetail(group)}
          style={{ height: 32 }}
          // Figma node is a flat 24px pill, but that reads too slim in context —
          // bumped to 32px (h-8) to match the intended chunkier proportions.
          className={`flex items-center justify-center gap-2 px-4 rounded-[16px] bg-[#00BCF1] text-white text-[14px] leading-[19px] font-bold transition-colors ${
            clickable
              ? "w-auto md:w-full pointer-events-none group-hover:bg-[#00A6D6]"
              : "w-full cursor-pointer hover:bg-[#00A6D6]"
          }`}
        >
          Lihat Detail
        </button>
      )}
    </div>
  );
};

// Data-driven replacement for the qualification / city-series schedule images.
// Cities sharing the same Kualifikasi + Series windows collapse into one card
// (names stacked), matching the Figma. Grid = flex-wrap so an incomplete last
// row centers (desktop 6×194px, mobile 2 cols).
const JadwalCards = ({ events = [] }) => {
  const navigate = useNavigate();
  // Lihat Detail on a multi-city group opens the "Pilih Kota" popup (centered
  // modal on desktop, bottom sheet on mobile — the shared BottomSheet handles
  // both) to pick a city; a single-city group navigates straight through (see
  // openDetail). Picking a popup card also navigates to that city.
  const [popupGroup, setPopupGroup] = useState(null);
  const goDetail = (e) => {
    const slug = e?.slug === "soc-2025" ? "soc" : e?.slug;
    if (slug) navigate(`/${slug}`, { state: { city: slug } });
  };
  const pickCity = (city) => {
    setPopupGroup(null);
    goDetail(city);
  };
  // A single-city group has nothing to disambiguate — go straight to that city;
  // only 2+ cities open the popup, keeping the 588px modal sized for the ≥2-card
  // Figma layout instead of one card floating in empty space.
  const openDetail = (group) => {
    analytics.sendEvent({
      event: "more_detail_schedule_pialabyu",
      title: group.map((c) => c?.region_name).join(" "),
    });
    group.length === 1 ? goDetail(group[0]) : setPopupGroup(group);
  };

  const groups = groupByWindow(sortByQualificationDate(events));

  return (
    <>
      <div className="flex flex-wrap items-center justify-center gap-[8px] md:gap-[16px_12px] mx-auto mt-8 w-full max-w-[1256px] px-6 md:px-4">
        {groups.map((group) => (
          <GroupCard key={group[0]?.id} group={group} onDetail={openDetail} />
        ))}
      </div>

      <BottomSheet open={!!popupGroup} onClose={() => setPopupGroup(null)}>
        {/* Desktop: fix the content to the Figma 588px so the shared BottomSheet
            container (max-w-600, sized to content) grows to it instead of
            shrinking to the ~450px of centered cards. Mobile stays full-width. */}
        <div
          className="px-4 md:w-[588px] md:px-8"
          // Sheet is anchored to the viewport bottom, so pad past the iOS home
          // indicator / mobile browser bottom bar — else the last card's button
          // sits under it. env(...) is 0 on desktop, keeping the 24px base there.
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
          }}
        >
          <div className="relative flex items-center justify-center mb-4 md:mb-6">
            <h3 className="text-[18px] leading-[21px] font-bold text-[#1F2D3D] mt-4">
              Pilih Kota
            </h3>
            <button
              type="button"
              onClick={() => setPopupGroup(null)}
              aria-label="Tutup"
              className="absolute right-0 p-1"
            >
              <img
                src={getStaticImg("icon/sa-close.svg")}
                alt=""
                className="w-8 h-8"
              />
            </button>
          </div>
          <div className="flex flex-col md:flex-row md:flex-wrap gap-3 md:gap-4 md:justify-center">
            {(popupGroup || []).map((city) => (
              <GroupCard
                key={city?.id}
                group={[city]}
                clickable
                widthClass="w-full md:w-[194px]"
                onDetail={([c]) => pickCity(c)}
              />
            ))}
          </div>
        </div>
      </BottomSheet>
    </>
  );
};

export default JadwalCards;
